import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Calculator, Loader2, PauseCircle, Target, ClipboardCheck } from "lucide-react";
import { fetchStreams } from "@/lib/api";
import { PROVINCES, PROVINCE_COLORS } from "@/lib/constants";
import { modelLabel, makeShow, STEP_INPUTS } from "@/lib/streamMode";

const SECTIONS = Object.keys(STEP_INPUTS).filter((k) => STEP_INPUTS[k]);
const sectionCount = (stream) => {
  const show = makeShow(stream.inputs);
  return SECTIONS.filter((k) => STEP_INPUTS[k].some(show)).length;
};

export default function StreamCalculator() {
  const [streams, setStreams] = useState(null);
  const [province, setProvince] = useState("ALL");
  const navigate = useNavigate();

  useEffect(() => {
    fetchStreams().then(setStreams).catch(() => setStreams([]));
  }, []);

  const filtered = useMemo(
    () => (streams || []).filter((s) => province === "ALL" || s.province === province),
    [streams, province]
  );

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-2 rounded-full bg-[#FEF2F2] px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#C8102E]">
            <Target className="h-3.5 w-3.5" /> Stream Score Calculator
          </span>
          <h1 className="mt-4 font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Which stream are you targeting?
          </h1>
          <p className="mt-2 text-slate-600">
            Pick one stream. We'll ask only the questions that stream scores and show your points, cutoff standing and criteria in detail.
          </p>
        </div>
        <Link to="/assessment" data-testid="calc-switch-to-eligibility"
          className="inline-flex items-center gap-1.5 self-start rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50">
          <ClipboardCheck className="h-4 w-4" /> Check all streams instead
        </Link>
      </div>

      <div className="mt-8 flex flex-wrap gap-2" data-testid="calc-province-filter">
        {[{ code: "ALL", name: "All provinces" }, ...PROVINCES].map((p) => (
          <button key={p.code} onClick={() => setProvince(p.code)} data-testid={`calc-province-${p.code}`}
            className={`min-h-[40px] rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${
              province === p.code ? "border-[#0F172A] bg-[#0F172A] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
            }`}>
            {p.name}
          </button>
        ))}
      </div>

      {!streams ? (
        <div className="mt-16 flex justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <div className="mt-6 grid gap-5 md:grid-cols-2 xl:grid-cols-3" data-testid="calc-stream-grid">
          {filtered.map((s) => (
            <StreamPick key={s.id} stream={s} onPick={() => navigate(`/assessment?stream=${s.id}`)} />
          ))}
        </div>
      )}
    </div>
  );
}

function StreamPick({ stream, onPick }) {
  const paused = stream.status !== "active";
  return (
    <div className="flex flex-col rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
      data-testid={`calc-stream-card-${stream.id}`}>
      <div className="flex items-start justify-between gap-3">
        <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-bold text-white"
          style={{ backgroundColor: PROVINCE_COLORS[stream.province] }}>{stream.province}</span>
        {paused && (
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
            <PauseCircle className="h-3.5 w-3.5" /> {stream.status}
          </span>
        )}
      </div>
      <h3 className="mt-3 font-heading text-lg font-semibold leading-snug text-slate-900">{stream.name}</h3>
      <p className="text-xs text-slate-500">{stream.province_name} · {stream.stream_group}</p>
      <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-slate-600">{stream.description}</p>
      <dl className="mt-4 grid grid-cols-2 gap-3 rounded-lg bg-slate-50 p-3 text-xs">
        <div>
          <dt className="font-semibold uppercase tracking-wider text-slate-400">Scoring</dt>
          <dd className="mt-0.5 font-medium text-slate-700">{modelLabel(stream)}</dd>
        </div>
        <div>
          <dt className="font-semibold uppercase tracking-wider text-slate-400">Cutoff</dt>
          <dd className="mt-0.5 font-mono-tech font-semibold text-slate-700">{stream.cutoff != null ? `~${stream.cutoff}` : "n/a"}</dd>
        </div>
      </dl>
      <p className="mt-3 text-xs text-slate-500">{sectionCount(stream)} of 5 questionnaire sections needed</p>
      <button onClick={onPick} data-testid={`calc-select-${stream.id}`}
        className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#C8102E] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#A60C24]">
        <Calculator className="h-4 w-4" /> Calculate my score <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
}
